import { Typography, useTheme } from '@mui/material'
import { DatePicker, LocalizationProvider, PickersDay } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import PropTypes from 'prop-types'
import { useCallback } from 'react'
import { cutCorners } from '../styles/themes/themeExtensions'

/**
 * DatePicker whose days carry a data-availability mark, shared by the
 * dashboard widgets (Operating Modes, Cycle Times, Vehicle Videos, Site CCTV)
 */
function DatePickerWithDataIndicators({
  label = 'Select Date',
  value,
  onChange,
  onMonthChange,
  availableDates,
  availableDatesCount,
  helperText,
  maxDate,
  minWidth = 200,
  showHelperText = false,
  ...otherProps
}) {
  const theme = useTheme()
  const accent = theme.palette.primary.main

  const CustomDay = useCallback(
    function CustomDay(props) {
      // eslint-disable-next-line react/prop-types
      const { day, outsideCurrentMonth, ...otherProps } = props
      // eslint-disable-next-line react/prop-types
      const dayString = day.format('YYYY-MM-DD')

      const hasData = Array.isArray(availableDates)
        ? availableDates.includes(dayString)
        : availableDates?.has(dayString)

      if (outsideCurrentMonth) {
        return <PickersDay {...otherProps} day={day} outsideCurrentMonth={outsideCurrentMonth} />
      }

      return (
        <PickersDay
          {...otherProps}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          sx={{
            ...otherProps.sx,
            position: 'relative',
            '&::after': hasData
              ? {
                  content: '""',
                  position: 'absolute',
                  bottom: 3,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 4,
                  height: 4,
                  backgroundColor: accent,
                }
              : {},
          }}
        />
      )
    },
    [availableDates, accent]
  )

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label={label}
          value={value}
          onChange={onChange}
          onMonthChange={onMonthChange}
          maxDate={maxDate}
          slots={{ day: CustomDay }}
          slotProps={{
            textField: {
              sx: {
                minWidth,
                '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
                '& .MuiOutlinedInput-root': {
                  color: theme.palette.text.primary,
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: accent },
                  '&.Mui-focused fieldset': { borderColor: accent },
                },
                '& .MuiInputBase-input': { color: theme.palette.text.primary },
                '& .MuiSvgIcon-root': { color: theme.palette.text.secondary },
              },
            },
            popper: {
              sx: {
                '& .MuiPaper-root': {
                  ...cutCorners(10, theme.palette.divider),
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: 'none',
                },
                '& .MuiPickersCalendarHeader-root': { color: theme.palette.text.primary },
                '& .MuiPickersArrowSwitcher-button': {
                  color: theme.palette.text.primary,
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                },
                '& .MuiDayCalendar-weekDayLabel': {
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                },
                '& .MuiPickersDay-root': {
                  borderRadius: 0,
                  color: theme.palette.text.primary,
                  backgroundColor: 'transparent',
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                  '&.MuiPickersDay-today': { border: `1px solid ${accent}` },
                  '&.Mui-selected': {
                    backgroundColor: accent,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 600,
                    '&:hover, &:focus': { backgroundColor: theme.palette.primary.dark },
                  },
                },
              },
            },
          }}
          {...otherProps}
        />
      </LocalizationProvider>

      {showHelperText && availableDatesCount > 0 && (
        <Typography
          variant='caption'
          sx={{ color: theme.palette.text.secondary, display: 'block', mt: 1 }}
        >
          {helperText || `Marked days have data • ${availableDatesCount} days available`}
        </Typography>
      )}
    </>
  )
}

DatePickerWithDataIndicators.propTypes = {
  label: PropTypes.string,
  value: PropTypes.object.isRequired, // dayjs object
  onChange: PropTypes.func.isRequired,
  onMonthChange: PropTypes.func,
  availableDates: PropTypes.oneOfType([PropTypes.instanceOf(Set), PropTypes.array]).isRequired,
  availableDatesCount: PropTypes.number.isRequired,
  helperText: PropTypes.string,
  maxDate: PropTypes.object, // dayjs object
  minWidth: PropTypes.number,
  showHelperText: PropTypes.bool,
}

export default DatePickerWithDataIndicators
